import { useCallback, useState } from 'react'
import { useWeb3React } from '@web3-react/core'
import { useAddPopup } from '../state/application/hooks'
import { NETWORK_CHAIN_ID } from '../connectors'
import { getChainConfig, toAddEthereumChainParameter } from '../constants/chains'

/** The wallet does not know this chain yet and must be asked to add it first. */
const CHAIN_NOT_ADDED = 4902
const USER_REJECTED_REQUEST = 4001

interface Eip1193Like {
  request?: (args: { method: string; params?: unknown[] }) => Promise<unknown>
}

/**
 * Asks the connected wallet to switch to the chain this app is configured for.
 *
 * Falls back to `wallet_addEthereumChain` when the wallet does not recognise the
 * network (code 4902), which is the normal path for BNB Chain Testnet and for users
 * on a fresh wallet profile.
 */
export default function useSwitchNetwork(): { switchNetwork: () => Promise<void>; switching: boolean } {
  const { library } = useWeb3React()
  const addPopup = useAddPopup()
  const [switching, setSwitching] = useState(false)

  const switchNetwork = useCallback(async () => {
    const config = getChainConfig(NETWORK_CHAIN_ID)
    // `library` is an ethers Web3Provider; the raw EIP-1193 provider is underneath it.
    const provider: Eip1193Like | undefined = library?.provider ?? library

    if (!config) {
      addPopup(
        { walletError: { message: `No configuration for chain ID ${NETWORK_CHAIN_ID}.` } },
        `switch-${Date.now()}`
      )
      return
    }
    if (!provider?.request) {
      addPopup(
        { walletError: { message: 'This wallet cannot switch networks automatically. Switch it manually.' } },
        `switch-${Date.now()}`
      )
      return
    }

    const hexChainId = `0x${config.chainId.toString(16)}`
    setSwitching(true)

    try {
      await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hexChainId }] })
    } catch (error) {
      const code = (error as { code?: number })?.code

      if (code === CHAIN_NOT_ADDED) {
        try {
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [toAddEthereumChainParameter(config)],
          })
        } catch (addError) {
          const addCode = (addError as { code?: number })?.code
          if (addCode !== USER_REJECTED_REQUEST) {
            addPopup(
              { walletError: { message: `Could not add ${config.name} to your wallet. Add it manually.` } },
              `switch-${Date.now()}`
            )
          }
        }
      } else if (code !== USER_REJECTED_REQUEST) {
        addPopup(
          { walletError: { message: `Could not switch to ${config.name}. Switch networks in your wallet.` } },
          `switch-${Date.now()}`
        )
      }
    } finally {
      setSwitching(false)
    }
  }, [library, addPopup])

  return { switchNetwork, switching }
}

/**
 * True when a wallet is connected but sitting on a different chain than the app is
 * configured for. Trading must be blocked in this state: addresses for the router,
 * multicall and token list are all chain-specific, and using one chain's addresses
 * against another silently produces wrong prices and failed transactions.
 */
export function useIsWrongNetwork(): boolean {
  const { account, chainId } = useWeb3React()
  return Boolean(account && chainId !== undefined && chainId !== NETWORK_CHAIN_ID)
}
